/**
 * LA TASA DEL DÍA, A LA VISTA (2026-10-02).
 *
 * El dueño: «la tasa BCV no se puede ver como tal en ningún lado; tendrías que
 * ir a Tasas. Debería estar en una esquinita en Ventas y en alguna parte del
 * Inicio». En Vender había una pastilla gris «1 USD = 853,4993 VES» que nadie
 * leía, y en Inicio la tasa estaba al final de la página.
 *
 * Esta pieza dice tres cosas de un vistazo:
 *
 *   · CUÁL es: «Tasa BCV» si la moneda la sigue sola, «Tasa del día» si se
 *     escribe a mano. No es lo mismo, y el cajero lo pregunta.
 *   · CUÁNTO vale, en bolívares por dólar (o la moneda que toque), con los
 *     mismos decimales con los que se cobra (ver `formatRate`).
 *   · DE CUÁNDO es: la fecha del BCV, o hace cuántos días se escribió. Una
 *     tasa vieja se pinta en ámbar: con ella se cobra de menos en cada venta.
 *
 * Dos tamaños: `esquina` (Vender, al lado del título) e `inicio` (con el euro
 * y la próxima tasa si el BCV ya la publicó).
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Landmark } from "lucide-react";
import { cx } from "@/components/ui";
import { monedas as endpointMonedas } from "@/lib/endpoints";
import { leerTasa } from "@/lib/direccionDeTasa";
import { formatRate } from "@/lib/money";
import type { TenantCurrency } from "@/lib/types";
import { useAuth } from "@/stores/auth";
import { usaElBCV } from "./TasaDelBCV";

const POR: Record<string, string> = { USD: "por dólar", EUR: "por euro" };

function fechaCorta(iso: string): string {
  const [, mes, dia] = iso.split("-");
  return dia && mes ? `${dia}/${mes}` : iso;
}

/**
 * La moneda cuya tasa se enseña: la que cobra el negocio además de la de los
 * precios. Si hay varias, la que sigue al BCV, y si no, el bolívar.
 */
function laQueSeEnsena(lista: TenantCurrency[]): TenantCurrency | undefined {
  const otras = lista.filter((m) => !m.is_primary && m.is_active && m.current_rate);
  return (
    otras.find((m) => m.tasa_automatica) ??
    otras.find((m) => m.currency_code === "VES") ??
    otras.find((m) => m.accepts_payment) ??
    otras[0]
  );
}

export function TasaDelDia({
  monedas,
  variante = "esquina",
  className,
}: {
  monedas: TenantCurrency[];
  variante?: "esquina" | "inicio";
  className?: string;
}) {
  const { puede } = useAuth();
  const principal = monedas.find((m) => m.is_primary);
  const moneda = laQueSeEnsena(monedas);

  // La misma consulta (y la misma clave) que el recuadro del BCV en Tasas.
  const { data: oficial } = useQuery({
    queryKey: ["tasa-bcv"],
    queryFn: endpointMonedas.tasaBCV,
    enabled: usaElBCV(monedas),
    staleTime: 10 * 60 * 1000,
  });

  if (!principal || !moneda) return null;
  const lectura = leerTasa(moneda.current_rate, principal.currency_code, moneda.currency_code);
  if (!lectura) return null;

  // «Bs 853,4993 por dólar»: el símbolo de la moneda que se recibe.
  const derecha = monedas.find((m) => m.currency_code === lectura.derecha);
  const simbolo = lectura.derecha === "VES" ? "Bs" : (derecha?.symbol ?? lectura.derecha);
  const por = POR[lectura.izquierda] ?? `por 1 ${lectura.izquierda}`;

  const delBCV = moneda.tasa_automatica;
  const enUso = oficial ? (oficial.la_de_manana && oficial.siguiente ? oficial.siguiente : oficial) : null;
  const dias = moneda.rate_age_days ?? 0;
  // Una tasa escrita a mano es vieja al día siguiente. La del BCV no sale los
  // fines de semana ni los feriados: con ella, a partir de 4 días algo falla.
  const vieja = delBCV ? dias >= 4 : dias >= 1;
  const cuando = delBCV && enUso
    ? `del ${fechaCorta(enUso.fecha)}`
    : dias === 0
      ? "de hoy"
      : `de hace ${dias} ${dias === 1 ? "día" : "días"}`;

  const titulo = `${delBCV ? "Tasa BCV" : "Tasa del día"} ${cuando}: 1 ${lectura.izquierda} = ${simbolo} ${formatRate(lectura.cuanto)}`;
  const contenido = (
    <>
      <span
        className={cx(
          "grid shrink-0 place-items-center rounded-full",
          variante === "inicio" ? "size-10" : "size-8",
          vieja ? "bg-warning/15 text-warning" : "bg-accent-soft text-accent",
        )}
      >
        <Landmark size={variante === "inicio" ? 18 : 15} aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span
          className={cx(
            "block whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide",
            vieja ? "text-warning" : "text-muted",
          )}
        >
          {delBCV ? "Tasa BCV" : "Tasa del día"} · {cuando}
        </span>
        <span className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className={cx("tabular font-semibold text-ink", variante === "inicio" ? "text-xl" : "text-base")}>
            {simbolo} {formatRate(lectura.cuanto)}
          </span>
          <span className="text-xs text-muted">{por}</span>
        </span>
        {/* En Inicio hay sitio para el euro y la próxima tasa del BCV. */}
        {variante === "inicio" && delBCV && oficial && (
          <span className="mt-0.5 block text-[11px] text-muted">
            {oficial.eur && lectura.izquierda === "USD" && (
              <span className="tabular">Euro: Bs {formatRate(oficial.eur)}</span>
            )}
            {oficial.siguiente && !oficial.la_de_manana && (
              <span className="tabular">
                {oficial.eur && lectura.izquierda === "USD" ? " · " : ""}
                Próxima ({fechaCorta(oficial.siguiente.fecha)}): Bs {formatRate(oficial.siguiente.usd)}
              </span>
            )}
          </span>
        )}
      </span>
    </>
  );

  const clases = cx(
    "inline-flex min-w-0 items-center gap-2.5 rounded-[var(--radius-card)] border text-left",
    variante === "inicio" ? "px-4 py-3" : "px-3 py-1.5",
    vieja ? "border-warning/50 bg-warning-soft" : "border-line bg-surface",
    className,
  );

  // Quien puede cambiar las tasas va a ellas con un toque; los demás solo la leen.
  return puede("currency.manage") ? (
    <Link to="/monedas" title={`${titulo}. Pulse para ver o actualizar las tasas.`} className={cx(clases, "transition-colors hover:border-accent/50")}>
      {contenido}
    </Link>
  ) : (
    <div title={titulo} className={clases}>
      {contenido}
    </div>
  );
}
